import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, message } from 'antd';
import { useTranslation } from 'react-i18next';

import DivisionTable from './DivisionTable';
import DivisionsDrawer from './DivisionsDrawer';

import CompanySettingsCard from '../CompanySettingsCard';
import SettingsCardHeader from '../SettingsCardHeader';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import SimpleNameRow from '../../common/editable/SimpleNameRow';

import {
  transferDivisions,
  updateDivision,
  updateDivisionCode,
  uploadDivisionImage,
  deleteDivisionImage,
} from '../state/settings.actions';
import DivisionCodeInput from './DivisionCodeInput';
import OtProfileSelect from '../../common/inputs/OtProfileSelect';

const { TabPane } = Tabs;

export default function DivisionsCard({
  id,
  name,
  code,
  otProfileId,
  imageURL,
  onDelete,
  users = [],
  teams = [],
  projects = [],
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const company = useSelector((state) => state.settings.company);
  const divisionCount = useSelector((state) => Object.values(state.settings.divisions).length);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [activeTab, setActiveTab] = useState('Users');
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [divisionImage, setDivisionImage] = useState(imageURL);
  const [imageUpdated, setImageUpdated] = useState(false);
  const inputFile = useRef();

  useEffect(() => {
    setDivisionImage(imageURL);
    setImageUpdated(false);
  }, [imageURL]);

  const onSelectChange = useCallback((shouldSetMove) => (newSelectedIds) => {
    setSelectedIds(newSelectedIds);
    setIsMoveMode(shouldSetMove && newSelectedIds.length > 0);
  }, []);

  const openDrawer = useCallback(() => setShowDrawer(true), []);
  const hideDrawer = useCallback(() => setShowDrawer(false), []);

  const onSubmit = useCallback(async ({ divisionId, keepOldDivision }) => {
    if (await dispatch(transferDivisions({
      type: activeTab.toLowerCase(),
      ids: selectedIds,
      divisionId: isMoveMode ? divisionId : id,
      keepOldDivision,
      oldDivisionId: id,
    }))) {
      setShowDrawer(false);
    }
  }, [isMoveMode, activeTab, selectedIds, id]);

  const onNameChanged = useCallback((newName) => {
    dispatch(updateDivision(id, { name: newName }));
  }, [id]);

  const onCodeUpdate = useCallback((newCode) => {
    dispatch(updateDivisionCode(id, newCode));
  }, [id]);

  const onOtProfileChange = (value) => {
    dispatch(updateDivision(id, { otProfileId: value || null }));
  };

  const onFileChange = useCallback((event) => {
    const {
      target: {
        files: [file] = [],
      } = {},
    } = event;

    if (file?.type?.includes('image')) {
      setDivisionImage(file);
      setImageUpdated(true);
      return;
    }

    if (file) {
      message.error('Division logo must be an image file.');
    }
  }, []);

  const onImageInputClick = useCallback(async () => {
    if (imageUpdated) {
      if (await dispatch(uploadDivisionImage(id, divisionImage))) setImageUpdated(false);
      return;
    }

    inputFile.current.click();
  }, [divisionImage, id, imageUpdated]);

  const onCancelImageUpload = useCallback(() => {
    setDivisionImage(imageURL);
    setImageUpdated(false);
  }, [imageURL]);

  const deleteImage = useCallback(async () => {
    if (await dispatch(deleteDivisionImage(id))) {
      setDivisionImage(null);
      setImageUpdated(false);
    }
  }, [id]);

  const onDeleteImage = useCallback(() => {
    CustomConfirmModal({
      title: 'Delete division logo?',
      content: 'This division will fall back to the default company logo.',
      onOk: deleteImage,
    });
  }, [deleteImage]);

  const hasRedesign = company?.settings?.uxRedesignPt1;
  const src = divisionImage instanceof File ? URL.createObjectURL(divisionImage) : divisionImage;

  return (
    <div className="divisions-card">
      <SettingsCardHeader title={name}>
        {onDelete && (
        <OnTraccrButton
          title="Delete"
          type="primary"
          onClick={onDelete}
        />
        )}
      </SettingsCardHeader>

      <CompanySettingsCard
        containerStyle={{
          top: hasRedesign ? 185 : 150,
          bottom: 20,
          padding: 0,
          marginBottom: '60px',
        }}
      >
        <div style={{ height: '100%', borderRadius: 8, overflow: 'hidden' }}>
          <SimpleNameRow
            name={name}
            onNameChanged={onNameChanged}
            title="Division Name"
            type="union"
          />

          <div style={{ padding: '15px' }}>
            <div style={{ marginBottom: 8 }}>Division Logo</div>
            {src ? (
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center' }}>
                <img
                  src={src}
                  style={{ maxWidth: 300, maxHeight: 100 }}
                  alt="division uploaded logo"
                />
                {!imageUpdated && (
                  <BorderlessButton
                    iconNode={<DeleteOutlined />}
                    style={{ width: 36, height: 36, color: "red" }}
                    onClick={onDeleteImage}
                  />
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 10 }}>using default company logo</div>
            )}
            <input type="file" hidden ref={inputFile} onChange={onFileChange} value="" accept="image/*" />
            {imageUpdated && (
              <OnTraccrButton
                title="Cancel"
                type="cancel"
                onClick={onCancelImageUpload}
                style={{ marginRight: 15 }}
              />
            )}
            {!src || imageUpdated ? (
              <OnTraccrButton
                title={imageUpdated ? 'Save Image' : 'Upload Image'}
                onClick={onImageInputClick}
              />
            ) : 
              null
            }
          </div>

          <DivisionCodeInput
            code={code}
            onCodeUpdate={onCodeUpdate}
            style={{
              padding: '15px',
            }}
          />

          <div style={{ padding: '0 15px 15px' }}>
            <span style={{ marginRight: 8 }}>OT Profile:</span>
            <OtProfileSelect
              value={otProfileId || undefined}
              onChange={onOtProfileChange}
              style={{ minWidth: 200 }}
            />
          </div>

          <Tabs
            style={{ height: 'calc(100% - 53px)' }}
            tabBarExtraContent={
              divisionCount > 1
                ? (
                  <OnTraccrButton
                    title={isMoveMode ? 'Transfer' : 'Add'}
                    type={isMoveMode ? 'back' : 'primary'}
                    onClick={openDrawer}
                  />
                )
                : null
            }
            tabBarStyle={{ padding: '0px 20px', marginBottom: 0 }}
            onChange={setActiveTab}
            activeKey={activeTab}
          >
            <TabPane tab="Users" key="Users">
              <DivisionTable
                data={users}
                type="users"
                onSelectChange={onSelectChange(true)}
              />
            </TabPane>
            <TabPane tab="Teams" key="Teams">
              <DivisionTable
                type="teams"
                data={teams}
                onSelectChange={onSelectChange(true)}
              />
            </TabPane>
            <TabPane tab={t('Project', { count: 0 })} key="Projects">
              <DivisionTable
                type="projects"
                data={projects}
                onSelectChange={onSelectChange(true)}
              />
            </TabPane>
          </Tabs>
        </div>
      </CompanySettingsCard>
      <DivisionsDrawer
        id={id}
        visible={showDrawer}
        onClose={hideDrawer}
        onSubmit={onSubmit}
        type={activeTab}
        isMoveMode={isMoveMode}
        name={name}
        onSelectChange={onSelectChange(false)}
      />
    </div>
  );
}

DivisionsCard.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  code: PropTypes.number,
  otProfileId: PropTypes.string,
  imageURL: PropTypes.string,
  onDelete: PropTypes.func,
  users: PropTypes.array.isRequired,
  teams: PropTypes.array.isRequired,
  projects: PropTypes.array.isRequired,
};

DivisionsCard.defaultProps = {
  code: null,
  otProfileId: null,
  imageURL: null,
  onDelete: null,
};
